import React, { useState, useEffect } from 'react';
import {
  getAdminTickets,
  getAdminTicket,
  updateTicketStatus,
  replyToTicket,
  getAdminErrorLogs,
  resolveErrorLog,
} from '../../services/api';

const STATUS_COLORS = {
  open: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-yellow-100 text-yellow-700',
  closed: 'bg-gray-100 text-gray-600',
};

const CATEGORY_LABELS = {
  bug: 'Bug',
  feature: 'Feature',
  audit_issue: 'Audit Issue',
  billing: 'Billing',
  general: 'General',
};

export default function AdminFeedback() {
  const [tab, setTab] = useState('tickets');
  const [tickets, setTickets] = useState([]);
  const [counts, setCounts] = useState({});
  const [selected, setSelected] = useState(null);
  const [replies, setReplies] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [errors, setErrors] = useState([]);
  const [errorCounts, setErrorCounts] = useState({});
  const [loading, setLoading] = useState(true);

  const loadTickets = () => {
    const params = {};
    if (filterStatus) params.status = filterStatus;
    if (filterCategory) params.category = filterCategory;
    getAdminTickets(params)
      .then((res) => {
        setTickets(res.data.tickets || []);
        setCounts(res.data.counts || {});
      })
      .catch(console.error);
  };

  const loadErrors = () => {
    getAdminErrorLogs({ resolved: 'false', limit: 50 })
      .then((res) => {
        setErrors(res.data.errors || []);
        setErrorCounts(res.data.counts || {});
      })
      .catch(console.error);
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([getAdminTickets({}), getAdminErrorLogs({ resolved: 'false', limit: 30 })])
      .then(([tRes, eRes]) => {
        setTickets(tRes.data.tickets || []);
        setCounts(tRes.data.counts || {});
        setErrors(eRes.data.errors || []);
        setErrorCounts(eRes.data.counts || {});
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (tab === 'tickets') loadTickets();
    else loadErrors();
  }, [filterStatus, filterCategory, tab]);

  const openTicket = async (id) => {
    try {
      const res = await getAdminTicket(id);
      setSelected(res.data.ticket);
      setReplies(res.data.replies || []);
    } catch {
      alert('Failed to load ticket');
    }
  };

  const handleStatus = async (status) => {
    if (!selected) return;
    try {
      await updateTicketStatus(selected.id, { status });
      setSelected({ ...selected, status });
      loadTickets();
    } catch {
      alert('Failed to update status');
    }
  };

  const handleReply = async () => {
    if (!selected || !replyText.trim()) return;
    try {
      await replyToTicket(selected.id, replyText.trim());
      setReplyText('');
      openTicket(selected.id);
    } catch {
      alert('Failed to send reply');
    }
  };

  const handleResolveError = async (id) => {
    const notes = window.prompt('Resolution notes (optional):');
    try {
      await resolveErrorLog(id, notes || '');
      loadErrors();
    } catch {
      alert('Failed to resolve error');
    }
  };

  if (loading) {
    return <p className="text-gray-500 py-12 text-center">Loading feedback...</p>;
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-800 mb-1">Feedback & Support</h2>
      <p className="text-gray-500 mb-6">User tickets and client-side error reports</p>

      <div className="flex gap-2 mb-6">
        <TabButton active={tab === 'tickets'} onClick={() => setTab('tickets')}>
          Tickets ({counts.open_count || 0} open)
        </TabButton>
        <TabButton active={tab === 'errors'} onClick={() => setTab('errors')}>
          Error Logs ({errorCounts.unresolved || 0} unresolved)
        </TabButton>
      </div>

      {tab === 'tickets' && (
        <div className="grid lg:grid-cols-2 gap-6">
          <div>
            <div className="flex gap-2 mb-4">
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm">
                <option value="">All statuses</option>
                <option value="open">Open</option>
                <option value="in_progress">In progress</option>
                <option value="closed">Closed</option>
              </select>
              <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm">
                <option value="">All categories</option>
                {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            <div className="glass-card rounded-2xl border border-teal-100/50 divide-y divide-gray-50 max-h-[600px] overflow-y-auto">
              {tickets.length === 0 ? (
                <p className="p-8 text-center text-gray-400">No tickets yet</p>
              ) : tickets.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => openTicket(t.id)}
                  className={`w-full text-left px-4 py-3 hover:bg-teal-50/50 transition ${selected?.id === t.id ? 'bg-teal-50' : ''}`}
                >
                  <div className="flex justify-between items-start gap-2">
                    <p className="font-medium text-gray-800 text-sm truncate">{t.subject}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${STATUS_COLORS[t.status] || ''}`}>
                      {t.status}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {CATEGORY_LABELS[t.category] || t.category} · {t.agency_name || t.user_email}
                  </p>
                  <p className="text-xs text-gray-400">{new Date(t.created_at).toLocaleString()}</p>
                </button>
              ))}
            </div>
          </div>

          <div className="glass-card rounded-2xl border border-teal-100/50 p-6 min-h-[400px]">
            {!selected ? (
              <p className="text-gray-400 text-center py-16">Select a ticket to view details</p>
            ) : (
              <>
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="font-bold text-gray-800">{selected.subject}</h3>
                    <p className="text-sm text-gray-500">
                      {selected.user_name || selected.user_email} · {selected.agency_name}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    {['open', 'in_progress', 'closed'].map((s) => (
                      <button key={s} type="button" onClick={() => handleStatus(s)}
                        className={`text-xs px-2 py-1 rounded ${selected.status === s ? 'bg-teal-600 text-white' : 'bg-gray-100'}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
                <p className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 rounded-xl p-4 mb-4">{selected.description}</p>
                {selected.current_page && (
                  <p className="text-xs text-gray-400 mb-4">Page: {selected.current_page}</p>
                )}
                <div className="space-y-2 mb-4 max-h-40 overflow-y-auto">
                  {replies.map((r) => (
                    <div key={r.id} className={`text-sm p-3 rounded-lg ${r.is_admin ? 'bg-teal-50 ml-4' : 'bg-gray-50 mr-4'}`}>
                      <p className="text-xs text-gray-500 mb-1">
                        {r.is_admin ? 'Admin' : `${r.first_name || ''} ${r.last_name || ''}`} · {new Date(r.created_at).toLocaleString()}
                      </p>
                      <p>{r.message}</p>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input value={replyText} onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Reply as admin..."
                    className="flex-1 border rounded-lg px-3 py-2 text-sm" />
                  <button type="button" onClick={handleReply}
                    className="bg-teal-600 text-white px-4 py-2 rounded-lg text-sm">Send</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {tab === 'errors' && (
        <div className="glass-card rounded-2xl border border-teal-100/50 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-teal-50/50 text-left text-gray-500">
                <th className="px-4 py-3">When</th>
                <th className="px-4 py-3">Severity</th>
                <th className="px-4 py-3">Message</th>
                <th className="px-4 py-3">User / Agency</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {errors.length === 0 ? (
                <tr><td colSpan="5" className="px-6 py-8 text-center text-gray-400">No unresolved errors</td></tr>
              ) : errors.map((e) => (
                <tr key={e.id} className="hover:bg-teal-50/30">
                  <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{new Date(e.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                      e.severity === 'critical' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>{e.severity}</span>
                  </td>
                  <td className="px-4 py-3 max-w-md truncate" title={e.message}>{e.message}</td>
                  <td className="px-4 py-3 text-gray-500">{e.user_email || '—'} {e.agency_name && `(${e.agency_name})`}</td>
                  <td className="px-4 py-3">
                    <button type="button" onClick={() => handleResolveError(e.id)}
                      className="text-teal-600 hover:underline text-xs">Resolve</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button type="button" onClick={onClick}
      className={`px-4 py-2 rounded-xl text-sm font-medium transition ${
        active ? 'bg-teal-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-teal-50'
      }`}>
      {children}
    </button>
  );
}
